import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { DiarySupabaseService } from '../services/DiarySupabaseService';
import { isSupabaseConfigured, supabase } from '../config/supabase';
import { SupabaseAuthService } from '../services/SupabaseAuthService';
import { AnalyticsService } from '../services/AnalyticsService';
import { TutorialService } from '../services/TutorialService';
import { NotificationService } from '../services/NotificationService';
import { DEMO_DIARY_PAGES, DEMO_DIARY_REMOVED_KEY } from '../data/demoDiaryPages';

export interface DiaryPage {
  id: string;
  title: string;
  type: 'page' | 'subpage' | 'folder';
  parentId?: string;
  content: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  order: number;
  isArchived: boolean;
}

export interface DiaryEntry {
  id: string;
  pageId: string;
  type: 'trade' | 'note' | 'analysis';
  content: string;
  metadata: {
    symbol?: string;
    entry?: string;
    exit?: string;
    pnl?: number;
    strategy?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export type SyncStatus = 'synced' | 'pending' | 'syncing' | 'error';

interface DiaryContextType {
  pages: DiaryPage[];
  entries: DiaryEntry[];
  selectedPage: DiaryPage | null;
  syncStatus: SyncStatus;
  setSelectedPage: React.Dispatch<React.SetStateAction<DiaryPage | null>>;
  addPage: (page: Omit<DiaryPage, 'id' | 'createdAt' | 'updatedAt'>) => Promise<DiaryPage> | DiaryPage;
  updatePage: (id: string, updates: Partial<DiaryPage>) => void;
  forceImmediateSave: (id: string, updates: Partial<DiaryPage>) => void;
  deletePage: (id: string) => void;
  addEntry: (entry: Omit<DiaryEntry, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateEntry: (id: string, updates: Partial<DiaryEntry>) => void;
  deleteEntry: (id: string) => void;
  getPageById: (id: string) => DiaryPage | undefined;
  getEntriesByPageId: (pageId: string) => DiaryEntry[];
  getSubpages: (parentId: string) => DiaryPage[];
  forceSyncNow: () => Promise<void>;
  createFolder?: (name: string, parentId?: string) => Promise<DiaryPage> | DiaryPage | Promise<void> | void;
}

const DiaryContext = createContext<DiaryContextType | undefined>(undefined);

export const useDiary = () => {
  const context = useContext(DiaryContext);
  if (context === undefined) {
    throw new Error('useDiary must be used within a DiaryProvider');
  }
  return context;
};

// Chiavi per il localStorage
const STORAGE_KEYS = {
  DIARY_PAGES: 'tradingspace_diary_pages',
  DIARY_ENTRIES: 'tradingspace_diary_entries',
  PENDING_SYNC: 'tradingspace_diary_pending_sync' // 🔧 FIX: Persist pending updates
} as const;

interface DiaryProviderProps {
  children: ReactNode;
}

export const DiaryProvider: React.FC<DiaryProviderProps> = ({ children }) => {
  const [pages, setPages] = useState<DiaryPage[]>([]);
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [selectedPage, setSelectedPage] = useState<DiaryPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('synced');
  const useSupabase = isSupabaseConfigured();

  // Refs for debounced sync and localStorage writes
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const localStorageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdatesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdatesRef = useRef<Map<string, Partial<DiaryPage>>>(new Map());

  // ===== SUPABASE FUNCTIONS =====

  const loadFromSupabase = async () => {
    try {
      setIsLoading(true);
      const loadedPages = await DiarySupabaseService.getUserPages();

      // Converti da formato Supabase (con blocks) a formato locale (con content)
      const convertedPages: DiaryPage[] = loadedPages.map(page => ({
        id: page.id,
        title: page.title,
        type: page.type,
        parentId: page.parentId,
        content: JSON.stringify(page.blocks || []), // Converti blocks array a JSON string
        tags: page.tags,
        createdAt: page.createdAt,
        updatedAt: page.updatedAt,
        order: page.order,
        isArchived: page.isArchived
      }));

      setPages(convertedPages);
      console.log('✅ Diario caricato da Supabase:', convertedPages.length, 'pagine');
    } catch (error) {
      console.error('❌ Errore caricamento da Supabase:', error);
      // Fallback a localStorage in caso di errore
      loadFromLocalStorage();
    } finally {
      setIsLoading(false);
    }
  };

  // ===== LOCALSTORAGE FUNCTIONS (FALLBACK) =====

  const saveToLocalStorage = (pagesData: DiaryPage[], entriesData: DiaryEntry[]) => {
    try {
      localStorage.setItem(STORAGE_KEYS.DIARY_PAGES, JSON.stringify(pagesData));
      localStorage.setItem(STORAGE_KEYS.DIARY_ENTRIES, JSON.stringify(entriesData));
      console.log('💾 Diario salvato in localStorage');
    } catch (error) {
      console.error('Errore nel salvataggio del diario:', error);
    }
  };

  // ✅ OPTIMIZATION: Debounced localStorage save (reduces writes by 70-80%)
  const debouncedLocalStorageSave = useCallback((pagesData: DiaryPage[], entriesData: DiaryEntry[]) => {
    if (localStorageTimeoutRef.current) {
      clearTimeout(localStorageTimeoutRef.current);
    }

    localStorageTimeoutRef.current = setTimeout(() => {
      saveToLocalStorage(pagesData, entriesData);
    }, 1000); // Write after 1 second of no changes
  }, []);

  // Sync version that returns data without setting state
  const loadFromLocalStorageSync = (): { pages: DiaryPage[]; entries: DiaryEntry[] } => {
    try {
      const savedPages = localStorage.getItem(STORAGE_KEYS.DIARY_PAGES);
      const savedEntries = localStorage.getItem(STORAGE_KEYS.DIARY_ENTRIES);

      return {
        pages: savedPages ? JSON.parse(savedPages) : [],
        entries: savedEntries ? JSON.parse(savedEntries) : []
      };
    } catch (error) {
      console.error('Errore nel caricamento da localStorage:', error);
      return { pages: [], entries: [] };
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const data = loadFromLocalStorageSync();
      setPages(data.pages);
      setEntries(data.entries);
      console.log('📖 Diario caricato da localStorage');
    } catch (error) {
      console.error('Errore nel caricamento del diario:', error);
    }
  };

  // ===== PENDING UPDATES PERSISTENCE (FIX: Survive reload) =====

  const savePendingUpdates = useCallback(() => {
    try {
      if (pendingUpdatesRef.current.size > 0) {
        const pending = Array.from(pendingUpdatesRef.current.entries());
        localStorage.setItem(STORAGE_KEYS.PENDING_SYNC, JSON.stringify(pending));
        console.log('💾 Pending sync salvati:', pending.length, 'updates');
      } else {
        // Clear if empty
        localStorage.removeItem(STORAGE_KEYS.PENDING_SYNC);
      }
    } catch (error) {
      console.error('❌ Errore salvataggio pending updates:', error);
    }
  }, []);

  // ✅ OPTIMIZATION: Debounced pending updates save (reduces writes)
  const debouncedPendingUpdatesSave = useCallback(() => {
    if (pendingUpdatesTimeoutRef.current) {
      clearTimeout(pendingUpdatesTimeoutRef.current);
    }

    pendingUpdatesTimeoutRef.current = setTimeout(() => {
      savePendingUpdates();
    }, 2000); // Write after 2 seconds of no changes
  }, [savePendingUpdates]);

  const loadPendingUpdates = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.PENDING_SYNC);
      if (saved) {
        const pending: [string, Partial<DiaryPage>][] = JSON.parse(saved);
        pending.forEach(([id, updates]) => {
          pendingUpdatesRef.current.set(id, updates);
        });
        console.log('📖 Pending sync caricati:', pending.length, 'updates');
        return pending.length > 0;
      }
      return false;
    } catch (error) {
      console.error('❌ Errore caricamento pending updates:', error);
      return false;
    }
  }, []);

  // ===== SYNC PENDING UPDATES (MUST BE BEFORE MOUNT EFFECT) =====

  const syncPendingUpdates = useCallback(async () => {
    if (!useSupabase || pendingUpdatesRef.current.size === 0) return;

    setSyncStatus('syncing');
    const updates = Array.from(pendingUpdatesRef.current.entries());
    pendingUpdatesRef.current.clear();

    try {
      // Sent together rather than one after another.
      //
      // This was a serial `for ... await`: every queued page waited for the
      // previous round trip to come back, so saving a diary entry took as long
      // as the queue was deep — seconds of "still saving" for something the
      // user finished typing. The requests do not depend on each other, so
      // there was never a reason to queue them. Same shape as the checkout
      // fix in a5edb91.
      //
      // Each result is still handled on its own terms below, phantom pages
      // included; only the waiting is shared.
      // In batches, not all at once: the queue is restored from localStorage
      // after time offline, and firing fifty simultaneous writes trades one
      // slow save for a set of rejected ones.
      const BATCH = 5;
      const outcomes: Array<{ id: string; pageUpdates: Partial<DiaryPage>; result: any }> = [];
      for (let i = 0; i < updates.length; i += BATCH) {
        const batch = await Promise.all(
          updates.slice(i, i + BATCH).map(async ([id, pageUpdates]) => {
            const supabaseUpdates: any = { ...pageUpdates };
            if (pageUpdates.content !== undefined) {
              supabaseUpdates.blocks = JSON.parse(pageUpdates.content);
              delete supabaseUpdates.content;
            }
            const result = await DiarySupabaseService.updatePage(id, supabaseUpdates);
            return { id, pageUpdates, result };
          })
        );
        outcomes.push(...batch);
      }

      for (const { id, pageUpdates, result } of outcomes) {
        if (!result.success) {
          // 🔍 DEBUG: Log full error structure to understand what we're dealing with
          console.log('🔍 DEBUG - Full error object for page', id, ':', {
            error: result.error,
            errorType: typeof result.error,
            errorKeys: result.error ? Object.keys(result.error) : [],
            errorStringified: JSON.stringify(result.error, null, 2)
          });

          // 🔧 FIX: Check multiple patterns for phantom page detection
          const errorString = JSON.stringify(result.error || {}).toLowerCase();
          const errorMessage = (typeof result.error === 'string' ? result.error : '').toLowerCase();
          const errorCode = (result.error as any)?.code;

          const isPhantomPage =
            errorCode === 'PGRST116' ||
            errorMessage.includes('0 rows') ||
            errorMessage.includes('cannot coerce') ||
            errorString.includes('pgrst116') ||
            errorString.includes('0 rows') ||
            errorString.includes('cannot coerce');

          if (isPhantomPage) {
            console.warn(`⚠️ PHANTOM PAGE DETECTED! Page ${id} doesn't exist in Supabase - removing from queue and local state`);

            // Remove from local pages state
            setPages(prev => {
              const filtered = prev.filter(p => p.id !== id);
              console.log(`🗑️ Removed phantom page from state. Pages before: ${prev.length}, after: ${filtered.length}`);
              return filtered;
            });

            // Don't re-add to pending queue
          } else {
            console.error('❌ Errore sync pagina (not a phantom page):', result.error);
            // Re-add to queue ONLY if it's not a phantom page error
            pendingUpdatesRef.current.set(id, pageUpdates);
          }
        }
      }

      // 🔧 FIX: Update persisted pending updates after sync
      savePendingUpdates();

      if (pendingUpdatesRef.current.size === 0) {
        setSyncStatus('synced');
        console.log('✅ Tutte le modifiche sincronizzate su Supabase');
      } else {
        setSyncStatus('error');
        console.error('⚠️ Alcune modifiche non sono state sincronizzate');
      }
    } catch (error) {
      console.error('❌ Errore sync a Supabase:', error);
      setSyncStatus('error');
      // 🔧 FIX: Save failed updates for retry on next load
      savePendingUpdates();
    }
  }, [useSupabase, savePendingUpdates]);

  // ===== LOAD DATA ON MOUNT =====

  useEffect(() => {
    const loadDataWithMerge = async () => {
      if (useSupabase) {
        try {
          setIsLoading(true);

          // 1. Load from localStorage first (immediate)
          const localData = loadFromLocalStorageSync();

          // 🔧 Auth check: if session is not valid, use localStorage only
          const { data: { user: authUser } } = await supabase.auth.getUser().catch(() => ({ data: { user: null } }));
          if (!authUser) {
            console.warn('⚠️ Sessione non autenticata — uso localStorage come fallback per il diario');
            if (localData.pages.length > 0) {
              setPages(localData.pages);
              setEntries(localData.entries);
            } else if (localStorage.getItem(DEMO_DIARY_REMOVED_KEY(null)) !== 'true') {
              setPages(DEMO_DIARY_PAGES);
            } else {
              setPages([]);
            }
            setIsLoading(false);
            return;
          }

          // 2. Load from Supabase
          const supabasePages = await DiarySupabaseService.getUserPages();
          const convertedPages: DiaryPage[] = supabasePages.map(page => ({
            id: page.id,
            title: page.title,
            type: page.type,
            parentId: page.parentId,
            content: JSON.stringify(page.blocks || []),
            order: page.order,
            color: (page as any).color,
            tags: (page as any).tags || [],
            isArchived: page.isArchived || false,
            createdAt: page.createdAt,
            updatedAt: page.updatedAt
          }));

          // 3. Merge: use whichever is more recent for each page
          const mergedPages = new Map<string, DiaryPage>();

          // Add Supabase pages first
          convertedPages.forEach(page => {
            mergedPages.set(page.id, page);
          });

          console.log('📊 Before merge - Supabase pages:', convertedPages.length);
          console.log('📊 Before merge - localStorage pages:', localData.pages.length);

          // 🔧 SAFETY: Only discard phantom pages if Supabase returned SOME data.
          // If Supabase returned 0 pages, it may be due to an auth/network error —
          // keep all localStorage pages as a fallback to prevent data loss.
          const supabaseReturnedData = convertedPages.length > 0;

          // Overwrite with localStorage if more recent
          const phantomPageIds: string[] = [];
          localData.pages.forEach(localPage => {
            const supabasePage = mergedPages.get(localPage.id);
            if (!supabasePage) {
              if (supabaseReturnedData) {
                // Supabase is working but this page is missing → real phantom
                console.warn(`⚠️ PHANTOM PAGE: "${localPage.title}" (${localPage.id}) exists in localStorage but NOT in Supabase!`);
                phantomPageIds.push(localPage.id);
              } else {
                // Supabase returned nothing → could be auth/network issue, keep local page
                console.warn(`⚠️ Supabase returned 0 pages — keeping local page "${localPage.title}" as safety fallback`);
                mergedPages.set(localPage.id, localPage);
              }
            } else if (new Date(localPage.updatedAt) > new Date(supabasePage.updatedAt)) {
              mergedPages.set(localPage.id, localPage);
              console.log(`📦 Using localStorage version for page "${localPage.title}" (more recent)`);
            }
          });

          // Clean up phantom pages from pending sync queue
          if (phantomPageIds.length > 0) {
            console.log(`🗑️ Cleaning up ${phantomPageIds.length} phantom pages from pending sync queue`);
            phantomPageIds.forEach(id => {
              pendingUpdatesRef.current.delete(id);
            });
            savePendingUpdates();
          }

          console.log('📊 After merge - Total pages:', mergedPages.size);

          if (mergedPages.size === 0) {
            // Inject demo pages for new users if not previously dismissed
            const user = await SupabaseAuthService.getCurrentUser().catch(() => null);
            const userId = user?.id || null;
            if (localStorage.getItem(DEMO_DIARY_REMOVED_KEY(userId)) !== 'true') {
              console.log('✨ Inietto pagine demo nel diario (nuovo utente)');
              setPages(DEMO_DIARY_PAGES);
            } else {
              setPages([]);
            }
          } else {
            setPages(Array.from(mergedPages.values()));
          }
          setEntries(localData.entries);
          setIsLoading(false);
          console.log('✅ Diario caricato con merge localStorage + Supabase');

          // 4. Load pending updates and sync immediately
          const hasPending = loadPendingUpdates();
          if (hasPending) {
            console.log('⚡ Syncing pending updates from previous session...');
            syncPendingUpdates();
          }
        } catch (error) {
          console.error('❌ Errore caricamento Supabase, uso localStorage:', error);
          loadFromLocalStorage();
          setIsLoading(false);
        }
      } else {
        const localData = loadFromLocalStorageSync();
        if (localData.pages.length === 0 && localStorage.getItem(DEMO_DIARY_REMOVED_KEY(null)) !== 'true') {
          console.log('✨ Inietto pagine demo nel diario (localStorage, nuovo utente)');
          setPages(DEMO_DIARY_PAGES);
          setEntries([]);
        } else {
          setPages(localData.pages);
          setEntries(localData.entries);
        }
        setIsLoading(false);
      }
    };

    loadDataWithMerge();
  }, [loadPendingUpdates, syncPendingUpdates]);

  // ===== AUTO-SAVE (solo per localStorage) =====

  useEffect(() => {
    if (!useSupabase && !isLoading && (pages.length > 0 || entries.length > 0)) {
      const timeoutId = setTimeout(() => {
        saveToLocalStorage(pages, entries);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [pages, entries, useSupabase, isLoading]);

  // ===== MULTI-TAB SYNC (FIX: Sync between tabs) =====

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Only react to changes in diary keys from OTHER tabs
      if (e.key === STORAGE_KEYS.DIARY_PAGES || e.key === STORAGE_KEYS.DIARY_ENTRIES) {
        console.log('🔄 Storage changed in another tab, reloading...');
        const data = loadFromLocalStorageSync();
        setPages(data.pages);
        setEntries(data.entries);
      }

      // Handle pending updates changes from other tabs
      if (e.key === STORAGE_KEYS.PENDING_SYNC) {
        console.log('⚡ Pending sync changed in another tab');
        loadPendingUpdates();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadPendingUpdates]);

  // ===== GESTIONE PAGINE =====

  const addPage = async (pageData: Omit<DiaryPage, 'id' | 'createdAt' | 'updatedAt'>) => {
    const createLocalPage = (reason?: unknown): DiaryPage => {
      if (reason) {
        console.warn('⚠️ Falling back to local diary page creation:', reason);
      }

      const newPage: DiaryPage = {
        ...pageData,
        id: `page_${Date.now()}`,
        content: pageData.content || JSON.stringify([]),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      localStorage.setItem(DEMO_DIARY_REMOVED_KEY(null), 'true');
      setPages(prev => [...prev.filter(p => !p.id.startsWith('demo_')), newPage]);
      return newPage;
    };

    if (useSupabase) {
      try {
        // Converti content (string) in blocks (array) per Supabase
        const blocks = pageData.content ? JSON.parse(pageData.content) : [];

        const result = await DiarySupabaseService.createPage({
          title: pageData.title,
          type: pageData.type,
          parentId: pageData.parentId,
          blocks: blocks,
          tags: pageData.tags,
          order: pageData.order,
          isArchived: pageData.isArchived
        });

        if (result.success && result.page) {
          // 🔧 FIX: Validate blocks before creating page
          const blocks = result.page.blocks || [];
          if (!Array.isArray(blocks)) {
            console.error('❌ Invalid blocks type:', typeof blocks, blocks);
            return createLocalPage('Invalid blocks structure from Supabase');
          }

          const content = JSON.stringify(blocks);
          console.log('✅ Converting blocks to content:', {
            blocksCount: blocks.length,
            contentLength: content.length,
            id: result.page.id
          });

          const newPage: DiaryPage = {
            id: result.page.id,
            title: result.page.title,
            type: result.page.type,
            parentId: result.page.parentId,
            content: content,
            tags: result.page.tags || [],
            createdAt: result.page.createdAt,
            updatedAt: result.page.updatedAt,
            order: result.page.order,
            isArchived: result.page.isArchived
          };

          // 🔧 FIX: Validate newPage before adding to state
          if (!newPage.id || !newPage.content) {
            console.error('❌ Created page missing required fields:', newPage);
            return createLocalPage('Page validation failed - missing id or content');
          }

          // Remove demo pages when user creates their first real page
          setPages(prev => [
            ...prev.filter(p => !p.id.startsWith('demo_')),
            newPage
          ]);
          console.log('✅ Pagina creata su Supabase:', newPage.id);

          // 📊 Track diary entry creation
          const user = await SupabaseAuthService.getCurrentUser();
          if (user) localStorage.setItem(DEMO_DIARY_REMOVED_KEY(user.id), 'true');
          if (user) {
            await AnalyticsService.trackDiaryEntryDB(user.id);
            // 🎯 Track tutorial step - first diary entry
            await TutorialService.completeStep(user.id, 'first_diary');
            // 🔔 Create notification
            await NotificationService.notifyDiaryEntryCreated(user.id);
          }

          return newPage;
        } else {
          console.error('❌ Errore creazione pagina:', result.error);
          return createLocalPage(result.error || 'Supabase returned no diary page');
        }
      } catch (error) {
        console.error('❌ Errore creazione pagina su Supabase:', error);
        return createLocalPage(error);
      }
    } else {
      // Fallback a creazione locale
      return createLocalPage();
    }
  };

  const createFolder = async (name: string, parentId?: string) => {
    const folder = await addPage({
      title: name,
      type: 'folder',
      parentId: parentId || '',
      content: JSON.stringify([]),
      tags: ['folder'],
      order: pages.length,
      isArchived: false,
    });

    return folder;
  };

  // ===== UPDATE PAGE =====

  const updatePage = (id: string, updates: Partial<DiaryPage>) => {
    // 1. Update local state immediately
    setPages(prev => {
      const updated = prev.map(page =>
        page.id === id
          ? { ...page, ...updates, updatedAt: new Date().toISOString() }
          : page
      );
      // ✅ OPTIMIZATION: Debounced localStorage write (was immediate)
      // Reduces writes from every keystroke to once per typing session
      debouncedLocalStorageSave(updated, entries);
      return updated;
    });

    // 2. If Supabase enabled, queue for debounced sync
    if (useSupabase) {
      // Merge with existing pending updates for this page
      const existing = pendingUpdatesRef.current.get(id) || {};
      pendingUpdatesRef.current.set(id, { ...existing, ...updates });

      // ✅ OPTIMIZATION: Debounced pending updates persistence (was immediate)
      debouncedPendingUpdatesSave();

      setSyncStatus('pending');

      // Clear existing timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      // Set new timeout for sync (500ms debounce - was 3000ms)
      syncTimeoutRef.current = setTimeout(() => {
        syncPendingUpdates();
      }, 500);

      console.log('💾 Salvato localmente, sync Supabase in 500ms...');
    }
  };

  // 🔧 FIX: Immediate save (no debounce) for component unmount scenarios
  const forceImmediateSave = (id: string, updates: Partial<DiaryPage>) => {
    console.log('🔧 forceImmediateSave called for page ID:', id, 'Updates:', updates);

    setPages(prev => {
      const pageExists = prev.find(p => p.id === id);
      if (!pageExists) {
        console.error('❌ ERROR: Trying to save page that does not exist!', id);
        return prev;
      }

      const updated = prev.map(page =>
        page.id === id
          ? { ...page, ...updates, updatedAt: new Date().toISOString() }
          : page
      );

      // ✅ IMMEDIATE localStorage write (bypasses debounce)
      saveToLocalStorage(updated, entries);
      console.log('💾 IMMEDIATE save to localStorage for page:', pageExists.title, 'ID:', id);
      return updated;
    });

    // Also sync to Supabase immediately if enabled
    if (useSupabase) {
      const existing = pendingUpdatesRef.current.get(id) || {};
      pendingUpdatesRef.current.set(id, { ...existing, ...updates });
      savePendingUpdates(); // Immediate save of pending updates
      syncPendingUpdates(); // Immediate sync to Supabase
      console.log('💾 IMMEDIATE sync to Supabase for page ID:', id);
    }
  };

  const deletePage = async (id: string) => {
    if (useSupabase) {
      try {
        const result = await DiarySupabaseService.deletePage(id);

        if (result.success) {
          setPages(prev => prev.filter(page => page.id !== id));
          setEntries(prev => prev.filter(entry => entry.pageId !== id));
          console.log('✅ Pagina eliminata da Supabase');
        } else {
          console.error('❌ Errore eliminazione pagina:', result.error);
        }
      } catch (error) {
        console.error('❌ Errore eliminazione pagina su Supabase:', error);
      }
    } else {
      // Fallback a eliminazione locale
      setPages(prev => prev.filter(page => page.id !== id));
      setEntries(prev => prev.filter(entry => entry.pageId !== id));
    }
  };

  // Gestione entries
  const addEntry = (entryData: Omit<DiaryEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newEntry: DiaryEntry = {
      ...entryData,
      id: `entry_${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setEntries(prev => [...prev, newEntry]);
  };

  const updateEntry = (id: string, updates: Partial<DiaryEntry>) => {
    setEntries(prev => prev.map(entry => 
      entry.id === id 
        ? { ...entry, ...updates, updatedAt: new Date().toISOString() }
        : entry
    ));
  };

  const deleteEntry = (id: string) => {
    setEntries(prev => prev.filter(entry => entry.id !== id));
  };

  // Funzioni di utilità
  const getPageById = (id: string) => {
    return pages.find(page => page.id === id);
  };

  const getEntriesByPageId = (pageId: string) => {
    return entries.filter(entry => entry.pageId === pageId);
  };

  const getSubpages = (parentId: string) => {
    return pages.filter(page => page.parentId === parentId && page.type === 'subpage');
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      // Sync any pending updates before unmounting
      if (pendingUpdatesRef.current.size > 0) {
        syncPendingUpdates();
      }
    };
  }, [syncPendingUpdates]);

  // Force immediate sync (e.g., when switching pages)
  const forceSyncNow = async () => {
    // Clear the debounce timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    // Sync immediately
    if (pendingUpdatesRef.current.size > 0) {
      await syncPendingUpdates();
      console.log('⚡ Forced immediate sync');
    }
  };

  // ===== SYNC SELECTED PAGE WITH PAGES ARRAY =====
  // When pages array updates (e.g., from updatePage), sync selectedPage
  useEffect(() => {
    if (selectedPage) {
      const updatedPage = pages.find(p => p.id === selectedPage.id);
      // Only update if content actually changed (not just reference)
      if (updatedPage && updatedPage.content !== selectedPage.content) {
        setSelectedPage(updatedPage);
        console.log('🔄 Synced selectedPage with pages array:', updatedPage.title);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]); // Only depend on pages, not selectedPage (avoid loop)

  const value: DiaryContextType = {
    pages,
    entries,
    selectedPage,
    syncStatus,
    setSelectedPage,
    addPage,
    updatePage,
    forceImmediateSave,
    deletePage,
    addEntry,
    updateEntry,
    deleteEntry,
    getPageById,
    getEntriesByPageId,
    getSubpages,
    forceSyncNow,
    createFolder
  };

  return (
    <DiaryContext.Provider value={value}>
      {children}
    </DiaryContext.Provider>
  );
};
